import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import ZippyOPSLogo from 'src/assets/img/labasservice_white.png';
import ZippopsBackground from 'src/assets/img/zippyops.mp4';

const URLConnectivity = () => {
    const [connectivityStatus, setConnectivityStatus] = useState([]);
    const [isChecked, setIsChecked] = useState(false);
    const [activeTest, setActiveTest] = useState(null);
    const [downloadSpeed, setDownloadSpeed] = useState(null);
    const [uploadSpeed, setUploadSpeed] = useState(null);
    const [ping, setPing] = useState(null);
    const [isTesting, setIsTesting] = useState(false);
    const [isConnectivityLoading, setIsConnectivityLoading] = useState(false);

    const statusRef = useRef([]);

    // Spinner Component
    const Spinner = () => (
        <div style={{
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            borderTopColor: 'white',
            animation: 'spin 1s ease-in-out infinite',
            marginRight: '8px'
        }}>
            {/* CSS for spin animation */}
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );

    // Modified checkConnectivity function
    const checkConnectivity = async (appName, url, retries = 1) => { // Reduced retries for quicker feedback
        // Function to check connectivity using iframe as a fallback for CORS issues
        const checkWithIframe = (url) => {
            return new Promise((resolve) => {
                const iframe = document.createElement('iframe');
                // Use a unique ID for each iframe to prevent conflicts
                const iframeId = `iframe-${appName}-${Date.now()}`;
                iframe.id = iframeId;
                iframe.src = url;
                iframe.style.display = 'none'; // Keep it hidden
                iframe.style.visibility = 'hidden'; // Ensure it's not rendered

                const timeoutId = setTimeout(() => {
                    document.body.removeChild(iframe);
                    resolve({ success: false, status: 'iframe Timeout', statusText: 'Iframe failed to load within timeout or encountered an error (CORS likely)' });
                }, 7000); // Increased timeout for iframe as it loads a full page

                iframe.onload = () => {
                    clearTimeout(timeoutId);
                    document.body.removeChild(iframe);
                    // Onload indicates the page *started* loading, but not necessarily a 200 OK.
                    // For CORS-blocked URLs, this is the best we can get without a proxy.
                    resolve({ success: true, status: '200 (via iframe)', statusText: 'Site reachable (via iframe)' });
                };

                iframe.onerror = () => {
                    clearTimeout(timeoutId);
                    document.body.removeChild(iframe);
                    // Onerror usually means a severe network issue or invalid URL
                    resolve({ success: false, status: 'iframe Error', statusText: 'Network or domain error (via iframe)' });
                };

                // Append to body to trigger load, then remove immediately
                document.body.appendChild(iframe);
            });
        };

        // --- Main Connectivity Check Logic ---
        let attempt = 0;
        while (attempt < retries) {
            try {
                // First, try with axios for URLs you might control or expect CORS headers for
                const response = await axios.get(url, { timeout: 5000 });
                // If successful (no CORS error and good status), record it and break
                statusRef.current.push({
                    appName,
                    status: response.status,
                    statusText: response.statusText
                });
                return { success: true, status: response.status, statusText: response.statusText };
            } catch (error) {
                // Check if it's a CORS error specifically
                // Axios throws a Network Error if it's CORS blocked, or if no response
                if (error.code === 'ERR_NETWORK' || (error.response && error.response.status === 0)) {
                    console.warn(`CORS/Network error for ${appName} (${url}). Falling back to iframe.`);
                    // Fallback to iframe for CORS-blocked URLs
                    const iframeResult = await checkWithIframe(url);
                    statusRef.current.push({
                        appName,
                        status: iframeResult.status,
                        statusText: iframeResult.statusText
                    });
                    return iframeResult; // Return the iframe result
                } else if (error.response) {
                    // Server responded with an error (e.g., 404, 500, not CORS)
                    statusRef.current.push({
                        appName,
                        status: error.response.status,
                        statusText: `Server error: ${error.response.statusText}`
                    });
                    return { success: false, status: error.response.status, statusText: error.response.statusText };
                } else if (error.request) {
                    // Request made, no response (e.g., timeout, network down)
                    statusRef.current.push({
                        appName,
                        status: 'No Response',
                        statusText: 'No response received from server or request timed out.'
                    });
                    return { success: false, status: 'No Response', statusText: 'No response received from server' };
                } else {
                    // Something else went wrong
                    statusRef.current.push({
                        appName,
                        status: 'Error',
                        statusText: `Error setting up request: ${error.message}`
                    });
                    return { success: false, status: 'Error', statusText: error.message };
                }
            }
            attempt++;
        }
        // If all retries fail, record the final error
        return { success: false, status: 'Failed', statusText: 'All attempts failed.' };
    };

    const checkInternetSpeed = async () => {
        setIsTesting(true);
        setActiveTest("speed");
        setDownloadSpeed(null);
        setUploadSpeed(null);
        setPing(null);
        
        try {
            // Test download speed
            let url_info = process.env.REACT_APP_FRONTEND_URL;

            // url_info = "http://localhost:3000"  // IF YOU WANT TO TEST FROM lOCAL 
            const testFileUrl = `${url_info}/static/test_file.bin`; // 1MB test file

            const startTime = performance.now();
            const response = await fetch(testFileUrl, { cache: "no-store" });
            await response.arrayBuffer(); // Ensure we download the entire file
            const endTime = performance.now();
            
            // Calculate download speed (Mbps)
            const fileSizeInBits = 1000000 * 8; // 1MB in bits
            const durationInSeconds = (endTime - startTime) / 1000;
            const downloadSpeedMbps = (fileSizeInBits / durationInSeconds) / (1024 * 1024);
            setDownloadSpeed(downloadSpeedMbps);
            
            // Test ping (latency)
            const pingStart = performance.now();
            await fetch(`${url_info}/get`, { cache: "no-store", method: "HEAD" });
            const pingEnd = performance.now();
            setPing(Math.round(pingEnd - pingStart));
            
            // Test upload speed (simplified - in a real app you'd upload a file)
            const uploadData = new Blob([new ArrayBuffer(500000)]); // 500KB test data
            const uploadStart = performance.now();
            await fetch(`${url_info}/post`, {
                method: "POST",
                body: uploadData,
                cache: "no-store"
            });
            const uploadEnd = performance.now();
            const uploadDuration = (uploadEnd - uploadStart) / 1000;
            const uploadSpeedMbps = (500000 * 8 / uploadDuration) / (1024 * 1024);
            setUploadSpeed(uploadSpeedMbps);
            
        } catch (error) {
            console.error("Error checking internet speed:", error);
            setDownloadSpeed("Error");
            setUploadSpeed("Error");
            setPing("Error");
        } finally {
            setIsTesting(false);
        }
    };


    const handleCheckConnectivity = async () => {
        setConnectivityStatus([]);
        setIsChecked(true);
        statusRef.current = [];
        setActiveTest("connectivity");
        setIsConnectivityLoading(true); // Start loading

        try {
            await Promise.all(urlsToCheck.map(item => checkConnectivity(item.appName, item.url)));
            setConnectivityStatus([...statusRef.current]);
        } finally {
            setIsConnectivityLoading(false); // Stop loading regardless of success/failure
        }
    };

    const renderConnectivityCheck = () => {
        if (activeTest !== "connectivity") return null;

        const desiredOrder = ['Application', 'Gateway', 'CodeShare', 'Compiler', 'Console', 'Report'];
        // Sort based on the desired order, handling cases where items might not be in desiredOrder
        const sortedStatus = [...connectivityStatus].sort((a, b) => {
            const indexA = desiredOrder.indexOf(a.appName);
            const indexB = desiredOrder.indexOf(b.appName);
            if (indexA === -1 && indexB === -1) return 0; // Both not in desired order, maintain original relative order
            if (indexA === -1) return 1; // a not in desired order, b is, so b comes first
            if (indexB === -1) return -1; // b not in desired order, a is, so a comes first
            return indexA - indexB;
        });

        // Determine if there are any *non-200* statuses recorded.
        // For iframe, '200 (via iframe)' is considered a success.
        const hasErrors = sortedStatus.some(item =>
            item.status !== 200 &&
            item.status !== '200 (via iframe)'
        );

        // Check if all URLs attempted have results
        const allChecksComplete = connectivityStatus.length === urlsToCheck.length; // Ensure urlsToCheck is accessible, or use its length directly

        return (
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '10px',
                padding: '20px',
                width: '60%',
                margin: '0',
                color: 'black',
                textAlign: 'left'
            }}>
                <table style={{ width: '100%', fontSize: '1.2rem', borderCollapse: 'collapse' }}>
                    <tbody>
                        {sortedStatus.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px 0' }}>{item.appName}</td>
                                <td style={{
                                    color: (item.status === 200 || item.status === '200 (via iframe)') ? 'green' : 'red',
                                    fontSize: '1.6rem',
                                    fontWeight: 'bold',
                                    textAlign: 'right',
                                    padding: '8px 0'
                                }}>
                                    {(item.status === 200 || item.status === '200 (via iframe)') ? "✔" : "✘"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {hasErrors ? (
                    <p style={{ color: 'red', marginTop: '15px' }}>NOTE: If there is any issue, contact support@zippyops.com</p>
                ) : (
                    allChecksComplete && !isConnectivityLoading && (
                        <p style={{ marginTop: '15px' }}>To access the Home page, <a href={process.env.REACT_APP_FRONTEND_URL} style={{
                            color: 'blue',
                            fontWeight: 'bold',
                            textDecoration: 'underline'
                        }}>click here.</a></p>
                    )
                )}
            </div>
        );
    };

    // Helper variable to make urlsToCheck accessible for allChecksComplete check
    const urlsToCheck = [
        { appName: 'Application', url: `${process.env.REACT_APP_FRONTEND_URL}` },
        { appName: 'Gateway', url: `${process.env.REACT_APP_API_URL}` },
        { appName: 'Console', url: `${process.env.REACT_APP_GUACAMOLE_URL}/#/` },
        { appName: 'Report', url: `${process.env.REACT_APP_GRAFANA_URL}` },
        { appName: 'CodeShare', url: `${process.env.REACT_APP_CODESHARE_URL}` },
        { appName: 'Compiler', url: `${process.env.REACT_APP_COMPILER_URL}` },
    ];


    return (
        <div style={{ position: 'relative', overflow: 'hidden', width: '100vw', height: '100vh' }}>
            <video autoPlay loop muted style={{ position: 'absolute', top: '50%', left: '50%', width: '100vw', height: '100vh', objectFit: 'cover', transform: 'translate(-50%, -50%)', zIndex: '-1' }}>
                <source src={ZippopsBackground} type="video/mp4" />
            </video>
            <div style={{ position: 'absolute', top: '20px', left: '100px', zIndex: '1' }}>
                <img src={ZippyOPSLogo} alt="ZippyOPS Logo" style={{ maxWidth: '240px', height: 'auto', borderRadius: '20px' }} />
            </div>
            <div style={{ position: 'relative', zIndex: '1', textAlign: 'center', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', height: '100%', marginTop: '70px' }}>

                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                    <button
                        onClick={handleCheckConnectivity}
                        disabled={isConnectivityLoading}
                        style={{
                            backgroundColor: isConnectivityLoading ? '#2E7D32' : '#4CAF50',
                            padding: '15px 32px',
                            fontSize: '1.2rem',
                            cursor: isConnectivityLoading ? 'wait' : 'pointer',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                            border: 'none',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            minWidth: '200px'
                        }}
                    >
                        {isConnectivityLoading ? (
                            <>
                                <Spinner />
                                Testing...
                            </>
                        ) : (
                            'Connectivity Test'
                        )}
                    </button>

                    <button
                        onClick={checkInternetSpeed}
                        disabled={isTesting}
                        style={{
                            backgroundColor: isTesting ? '#cccccc' : '#2196F3',
                            padding: '15px 32px',
                            fontSize: '1.2rem',
                            cursor: isTesting ? 'not-allowed' : 'pointer',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                            border: 'none',
                            color: 'white'
                        }}
                    >
                        {isTesting ? 'Testing...' : 'Speed Test'}
                    </button>
                </div>

                {activeTest === "speed" && (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '10px',
                        padding: '20px',
                        textAlign: 'center',
                        margin: '20px auto',
                        color: 'black',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        width: '60%'
                    }}>
                        <h3 style={{ marginBottom: '20px' }}>Connectivity Speed Test</h3>

                        {isTesting ? (
                            <p>Testing your Connectivity speed...</p>
                        ) : (
                            <>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    flexWrap: 'wrap',
                                    gap: '20px',
                                    marginBottom: '20px'
                                }}>
                                    {/* Download Speed */}
                                    <div style={{
                                        backgroundColor: '#f0f8ff',
                                        padding: '15px',
                                        borderRadius: '8px',
                                        minWidth: '150px',
                                        flex: '1'
                                    }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#2196F3' }}>Download Speed</h4>
                                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '5px 0', color: '#2196F3' }}>
                                            {downloadSpeed === null ? "--" :
                                                downloadSpeed === "Error" ? "Error" : downloadSpeed.toFixed(2)}
                                        </p>
                                        <p style={{ margin: '0', color: '#666' }}>Mbps</p>
                                    </div>

                                    {/* Upload Speed */}
                                    <div style={{
                                        backgroundColor: '#f0fff0',
                                        padding: '15px',
                                        borderRadius: '8px',
                                        minWidth: '150px',
                                        flex: '1'
                                    }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>Upload Speed</h4>
                                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '5px 0', color: '#4CAF50' }}>
                                            {uploadSpeed === null ? "--" :
                                                uploadSpeed === "Error" ? "Error" : uploadSpeed.toFixed(2)}
                                        </p>
                                        <p style={{ margin: '0', color: '#666' }}>Mbps</p>
                                    </div>

                                    {/* Ping (Latency) */}
                                    <div style={{
                                        backgroundColor: '#fff8f0',
                                        padding: '15px',
                                        borderRadius: '8px',
                                        minWidth: '150px',
                                        flex: '1'
                                    }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#FF9800' }}>Ping</h4>
                                        <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '5px 0', color: '#FF9800' }}>
                                            {ping === null ? "--" : ping === "Error" ? "Error" : ping}
                                        </p>
                                        <p style={{ margin: '0', color: '#666' }}>ms</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {renderConnectivityCheck()}
            </div>
        </div>
    );
};

export default URLConnectivity;